import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Heart, Search, X } from "lucide-react";
import type { Recipe, SearchDocument, SearchHit, SearchResponse } from "@/types";
import type { Lang } from "@/i18n";
import { T } from "@/i18n";
import { RecipeCard } from "./RecipeCard";
import { RecipeCardSkeleton } from "./RecipeCardSkeleton";
import { LangToggle } from "./LangToggle";
import { CATEGORIES, DIFFICULTIES, SORTS, TAGS, toApiSlug, type SortValue } from "@/filters";
import { langPath, replaceQuery } from "@/router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useFavorites } from "@/favorites";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ALL = "all";
const PER_PAGE = 20;

function docToRecipe(doc: SearchDocument): Recipe {
  return { ...doc, categories: doc.categories_list, tags: doc.tags_list } as unknown as Recipe;
}

// Read the URL fresh at mount so refresh/back restore filters (lazy per-mount).
function readUrl() {
  return new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
}

export function RecipeList({ lang }: { lang: Lang }) {
  const [search, setSearch] = useState(() => readUrl().get("q") ?? "");
  const [query, setQuery] = useState(() => readUrl().get("q") ?? "");
  const [sort, setSort] = useState<SortValue>(() => (readUrl().get("sort") as SortValue) ?? "newest");
  const [category, setCategory] = useState(() => readUrl().get("category") ?? ALL);
  const [tags, setTags] = useState<string[]>(() => readUrl().get("tags")?.split(",").filter(Boolean) ?? []);
  const [difficulty, setDifficulty] = useState(() => readUrl().get("difficulty") ?? "");
  const [favoritesOnly, setFavoritesOnly] = useState(() => readUrl().get("favorites") === "1");
  const [page, setPage] = useState(() => Number(readUrl().get("page")) || 1);

  const { ids: favoriteIds } = useFavorites();

  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [found, setFound] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const firstRun = useRef(true);

  const numPages = Math.max(1, Math.ceil(found / PER_PAGE));
  const hasFilters = query !== "" || category !== ALL || tags.length > 0 || difficulty !== "" || favoritesOnly;

  // Debounce the search box → query (resetting to page 1).
  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(q => {
        if (q !== search.trim()) setPage(1);
        return search.trim();
      });
    }, 350);
    return () => clearTimeout(id);
  }, [search]);

  // Canonical (English-slug) params shared by URL + LangToggle targets.
  function buildParams() {
    const p = new URLSearchParams();
    if (query) p.set("q", query);
    if (sort !== "newest") p.set("sort", sort);
    if (category !== ALL) p.set("category", category);
    if (tags.length) p.set("tags", tags.join(","));
    if (difficulty) p.set("difficulty", difficulty);
    if (favoritesOnly) p.set("favorites", "1");
    if (page > 1) p.set("page", String(page));
    return p;
  }

  useEffect(() => {
    const params = buildParams();
    replaceQuery(params.toString());

    // Translate canonical slugs → language-specific API slugs.
    const api = new URLSearchParams();
    api.set("lang", lang);
    if (query) api.set("q", query);
    if (sort !== "newest") api.set("sort", sort);
    if (category !== ALL) api.set("category", toApiSlug(CATEGORIES, category, lang));
    if (tags.length) api.set("tags", tags.map(id => toApiSlug(TAGS, id, lang)).join(","));
    if (difficulty) api.set("difficulty", difficulty);
    if (favoritesOnly) api.set("ids", favoriteIds.join(","));
    if (page > 1) api.set("page", String(page));

    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    fetch(`/api/search?${api}`, { signal: ctrl.signal })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<SearchResponse>;
      })
      .then(json => {
        setHits(json.hits ?? []);
        setFound(json.found);
        if (!firstRun.current) {
          topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        firstRun.current = false;
      })
      .catch(err => {
        if (err.name !== "AbortError") setError(err.message);
      })
      .finally(() => setLoading(false));

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sort, category, tags, difficulty, page, lang, favoritesOnly, favoriteIds.join(",")]);

  function toggleTag(id: string) {
    setTags(t => (t.includes(id) ? t.filter(x => x !== id) : [...t, id]));
    setPage(1);
  }

  function clearAll() {
    setSearch("");
    setQuery("");
    setCategory(ALL);
    setTags([]);
    setDifficulty("");
    setFavoritesOnly(false);
    setPage(1);
  }

  const qs = buildParams().toString();
  const langTargets: Record<Lang, string> = {
    en: langPath("en") + (qs ? `?${qs}` : ""),
    fr: langPath("fr") + (qs ? `?${qs}` : ""),
  };

  return (
    <div className="min-h-screen w-full">
      <header ref={topRef} className="border-b bg-card/60 backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Recettes</h1>
              <p className="mt-1 text-muted-foreground">
                {loading && hits === null
                  ? T.loading[lang]
                  : `${found.toLocaleString()} ${found === 1 ? T.recipe[lang] : T.recipes[lang]}${
                      query ? ` ${T.resultsFor[lang]} “${query}”` : ""
                    }`}
              </p>
            </div>
            <LangToggle lang={lang} targets={langTargets} />
          </div>

          {/* search + sort + category */}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <div className="relative w-full sm:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder={T.searchPlaceholder[lang]}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={sort} onValueChange={v => { setSort(v as SortValue); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map(s => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label[lang]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={category} onValueChange={v => { setCategory(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-52">
                <SelectValue placeholder={T.allCategories[lang]} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{T.allCategories[lang]}</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c[lang].name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* difficulty + tag chips */}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => { setFavoritesOnly(v => !v); setPage(1); }}
              aria-pressed={favoritesOnly}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${
                favoritesOnly ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
              }`}
            >
              <Heart className={`size-3 ${favoritesOnly ? "fill-current" : ""}`} /> {T.favoritesOnly[lang]}
              {favoriteIds.length > 0 && <span className="opacity-70">({favoriteIds.length})</span>}
            </button>
            <span className="mx-1 h-4 w-px bg-border" />
            {DIFFICULTIES.map(d => {
              const active = difficulty === d.value;
              return (
                <button
                  key={d.value}
                  onClick={() => { setDifficulty(active ? "" : d.value); setPage(1); }}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                    active ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
                  }`}
                >
                  {d.label[lang]}
                </button>
              );
            })}
            <span className="mx-1 h-4 w-px bg-border" />
            {TAGS.map(tg => {
              const active = tags.includes(tg.id);
              return (
                <button
                  key={tg.id}
                  onClick={() => toggleTag(tg.id)}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                    active ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
                  }`}
                >
                  {tg[lang].name}
                </button>
              );
            })}
            {hasFilters && (
              <button
                onClick={clearAll}
                className="ml-1 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="size-3" /> {T.clear[lang]}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-destructive">
            {T.failed[lang]} {error}
          </div>
        )}

        {hits === null && !error && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <RecipeCardSkeleton key={i} />
            ))}
          </div>
        )}

        {hits !== null && hits.length === 0 && !loading && (
          <p className="py-20 text-center text-muted-foreground">
            {favoritesOnly && favoriteIds.length === 0
              ? T.noFavorites[lang]
              : hasFilters
                ? T.noMatch[lang]
                : T.none[lang]}
          </p>
        )}

        {hits !== null && hits.length > 0 && (
          <div
            className={`grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 ${
              loading ? "opacity-60 transition-opacity" : ""
            }`}
          >
            {hits.map(hit => (
              <RecipeCard
                key={hit.document.id}
                recipe={docToRecipe(hit.document)}
                lang={lang}
                titleHtml={hit.highlight?.title?.snippet}
              />
            ))}
          </div>
        )}

        {hits !== null && numPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-4">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))}>
              <ChevronLeft /> {T.prev[lang]}
            </Button>
            <span className="text-sm text-muted-foreground">
              {T.page[lang]} {page} {T.of[lang]} {numPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= numPages || loading} onClick={() => setPage(p => p + 1)}>
              {T.next[lang]} <ChevronRight />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
